/* Our own columns, against somebody else's rows.
 *
 * The schools layer belongs to the GIS and is read only here. What Witt
 * O'Brien's needs to record against a school, the ICD number, who owns it, what
 * was agreed on Tuesday, has no field in that layer and never will. So it goes
 * in a tracker of our own, one row per school, joined on the school's code.
 *
 * Two rules make this honest. Our columns never overwrite theirs: they sit
 * under their own keys and are drawn as ours. And a school that has arrived in
 * the layer since we started still appears, with our side blank, rather than
 * dropping off the map because nobody has typed anything about it yet.
 *
 * Pure.
 */

export const OURS = "our:";

export const joinKey = (v) => String(v ?? "").trim().toLowerCase();

/* A GIS keeps its own row numbers, and they are not the program's. Rebuild
   the layer from a new spreadsheet and every one of them changes, which would
   silently unhook every row we had written against them. Seven of the
   twenty eight schools already have a null OBJECTID. So these are offered
   last, never first. */
const INTERNAL = /^(fid|objectid|globalid|esri_oid|oid|shape.*)$/i;

/* Fields that are somebody's name for the thing rather than a database's:
   a school code, a contract number, a reference. These survive a rebuild
   because a person typed them for a reason. */
const BUSINESS = /(code|number|no\b|ref|reference|id_)/i;

/* A column both sides can be matched on: filled on every row and different on
   every row. On the schools layer that is the school code. */
export function keyCandidates(columns = [], rows = []) {
  if (!rows.length) return [];
  const usable = columns.filter((c) => {
    if (c.key.startsWith("_") || c.key.startsWith(OURS)) return false;
    if (c.type === "date") return false;
    const values = rows.map((r) => joinKey(r[c.key]));
    if (values.some((v) => v === "")) return false;
    return new Set(values).size === rows.length;
  });

  const rank = (c) => {
    const name = `${c.key} ${c.label}`;
    if (INTERNAL.test(String(c.key).trim())) return 3;
    if (BUSINESS.test(name)) return 0;
    return c.type === "number" ? 2 : 1;
  };

  return usable
    .map((c, i) => ({ c, i, r: rank(c) }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map((x) => x.c);
}

/* Our columns, renamed so they cannot collide with the layer's own. A layer
   with a Status field and a tracker with a Status field are two different
   questions and both have to survive. */
export const ourColumns = (columns = []) => columns.map((c) => ({
  ...c, key: `${OURS}${c.key}`, ours: true,
}));

export function joinToLayer({
  layerRows = [], layerKey, ourRows = [], ourKey,
} = {}) {
  if (!layerKey || !ourKey) {
    return { rows: layerRows, matched: 0, unmatched: layerRows.length, orphans: [] };
  }

  const mine = new Map();
  for (const r of ourRows) {
    const k = joinKey(r[ourKey]);
    if (k && !mine.has(k)) mine.set(k, r);
  }

  let matched = 0;
  const used = new Set();
  const rows = layerRows.map((row) => {
    const k = joinKey(row[layerKey]);
    const ours = mine.get(k);
    if (!ours) return { ...row, _ourId: null };
    matched += 1;
    used.add(k);
    const merged = { ...row, _ourId: ours._id ?? null };
    for (const [field, value] of Object.entries(ours)) {
      if (field.startsWith("_")) continue;
      merged[`${OURS}${field}`] = value;
    }
    return merged;
  });

  /* Rows of ours that match nothing in the layer. Usually a school that was
     renamed or removed, and always worth saying out loud rather than silently
     dropping somebody's typing. */
  const orphans = ourRows.filter((r) => !used.has(joinKey(r[ourKey])));

  return { rows, matched, unmatched: layerRows.length - matched, orphans };
}

/* One row of ours for every row in the layer, carrying only what is needed to
   line the two up. Every other column is whatever the person building the
   tracker asked for, and starts empty. */
export function seedRows({ layerRows = [], layerKey, ourKey, labelKey, labelAs }) {
  return layerRows.map((row) => {
    const seeded = { [ourKey]: row[layerKey] ?? "" };
    if (labelAs && labelKey) seeded[labelAs] = row[labelKey] ?? "";
    return seeded;
  });
}
